import { useEffect, useState } from "react";
import * as E from "@numi/engine";
import { api } from "../api.ts";
import { useApp, updateChild, autoRead, calmMode, toast, refreshMe } from "../store.ts";

/**
 * Learning comfort, in the grown-ups corner only. Behaviour questions (never a diagnosis) suggest presets;
 * every switch can be fine-tuned. Off by default, so nothing changes for a child unless a grown-up chooses it.
 */
export function ComfortSettings() {
  const s = useApp(); const c = s.child!; const cf = c.settings.comfort || {}; const on = new Set(cf.presets || []);
  const [ans, setAns] = useState<Partial<Record<E.Preset, boolean>>>({});
  const sugg = E.suggestPresets(ans).filter((p) => !on.has(p));
  const setPreset = (p: E.Preset, v: boolean) => {
    updateChild({ settings: { ...c.settings, ...E.presetPatch(c.settings, p, v) } });
    toast(v ? `${E.PRESETS[p].title} is on` : `${E.PRESETS[p].title} is off`);
  };
  const value = (k: E.SwitchKey) => k === "autoRead" ? autoRead() : k === "calm" ? calmMode() : !!cf[k];
  const setSwitch = (k: E.SwitchKey, v: boolean) => {
    if (k === "autoRead" || k === "calm") { updateChild({ settings: { ...c.settings, [k]: v } }); return; }
    const next: E.Comfort = { ...cf }; if (v) next[k] = true; else delete next[k];
    updateChild({ settings: { ...c.settings, comfort: next } });
  };
  return <>
    <h3 className="gh">Learning comfort</h3>
    <p className="note">Small changes that make Numi easier for some children. Only grown-ups see these settings. {c.name} keeps the same lessons and earns stars exactly the same way.</p>
    <div className="cqs">{E.COMFORT_QUESTIONS.map(({ preset, q }) => <div key={preset} className="cq"><span>{q}</span>
      <span className="row"><button className={`chipbtn ${ans[preset] === true ? "on" : ""}`} aria-pressed={ans[preset] === true} onClick={() => setAns({ ...ans, [preset]: true })}>Yes</button>
        <button className={`chipbtn ${ans[preset] === false ? "on" : ""}`} aria-pressed={ans[preset] === false} onClick={() => setAns({ ...ans, [preset]: false })}>Not really</button></span></div>)}</div>
    {sugg.length > 0 && <div className="csugg"><span>Suggested for {c.name}: <b>{sugg.map((p) => E.PRESETS[p].title).join(" and ")}</b></span>
      <button className="btn yellow sm" onClick={() => { let st = c.settings; for (const p of sugg) st = { ...st, ...E.presetPatch(st, p, true) }; updateChild({ settings: st }); toast("Turned on. You can change it any time."); }}>Turn on</button></div>}
    <div className="pgrid">{E.PRESET_IDS.map((p) => <label key={p} className={`pcard ${on.has(p) ? "on" : ""}`}>
      <input type="checkbox" checked={on.has(p)} onChange={(e) => setPreset(p, e.target.checked)} />
      <span><b>{E.PRESETS[p].title}</b><small>{E.PRESETS[p].blurb}</small></span></label>)}</div>
    <details className="ctune"><summary>Fine-tune each setting</summary>
      <div className="toggles">{E.SWITCHES.map(({ key, label }) => <label key={key} className="tog"><input type="checkbox" checked={value(key)} onChange={(e) => setSwitch(key, e.target.checked)} /> {label}</label>)}</div>
      <p className="note">Read aloud and no timers are on by default for {E.BOARDS[c.board].gradeWord} 1–2.</p>
    </details>
  </>;
}

interface Week { week: { questions: number; firstTry: number; days: number; minutes: number }; quiet: boolean; headline: string; highs: string[]; next: string[]; growth: string | null; dinner: { ask: string; answer: string } | null; asked: string | null }
/** "This week": the same highs, next steps and numbers as the Sunday email. */
export function WeekCard() {
  const s = useApp(); const [w, setW] = useState<Week | null>(null);
  useEffect(() => { setW(null); api<Week>(`/children/${s.child!.id}/week`).then(setW).catch(() => {}); }, [s.child!.id]);
  if (!w) return null;
  return <div className="wcard">
    <h3 className="gh" style={{ marginTop: 0 }}>This week</h3>
    <p>{w.headline}</p>
    {w.highs.length > 0 && <ul className="wl hi">{w.highs.map((x, i) => <li key={i}>{x}</li>)}</ul>}
    {w.next.length > 0 && <><b className="wsub">Where Numi is helping next</b><ul className="wl nx">{w.next.map((x, i) => <li key={i}>{x}</li>)}</ul></>}
    {w.growth && <p className="note">{w.growth}</p>}
    {w.dinner && <p className="wdin"><b>Try at dinner:</b> {w.dinner.ask} <small>(Answer: {w.dinner.answer})</small></p>}
  </div>;
}

/** The Sunday weekly report email: on unless the parent turns it off. */
export function WeeklyToggle() {
  const s = useApp(); const on = s.parent?.weekly !== false;
  return <label className="tog"><input type="checkbox" checked={on} onChange={async (e) => { try { await api("/auth/prefs", { body: { weekly: e.target.checked } }); await refreshMe(); toast(e.target.checked ? "Weekly report on: Sunday evenings" : "Weekly report off"); } catch (err: any) { toast(err.message); } }} /> Email me a weekly report on Sunday evenings</label>;
}
